import { WebFileService, WebNotificationService } from '../utils/platform/web'
import { TauriFileService, TauriNotificationService } from '../utils/platform/tauri'
import type { FileSaveOptions, FileSaveResult, FileLoadResult } from '../utils/platform/types'

type Platform = 'tauri' | 'electron' | 'web' | 'unknown'

class PlatformService {
  private platform: Platform
  private fileService!: WebFileService | TauriFileService
  private notificationService!: WebNotificationService | TauriNotificationService

  constructor() {
    this.platform = this.detectPlatform()
    this.initService()
  }

  private detectPlatform(): Platform {
    if (typeof window === 'undefined') return 'unknown'
    if ((window as any).__TAURI__) return 'tauri'
    if ((window as any).electron) return 'electron'
    return 'web'
  }

  private initService(): void {
    switch (this.platform) {
      case 'tauri':
        try {
          this.fileService = new TauriFileService()
          this.notificationService = new TauriNotificationService()
        } catch {
          // 降级到 Web 服务
          this.fileService = new WebFileService()
          this.notificationService = new WebNotificationService()
          this.platform = 'web'
        }
        break
      case 'electron':
        // Electron 实现可以后续添加
        this.fileService = new WebFileService()
        this.notificationService = new WebNotificationService()
        break
      default:
        this.fileService = new WebFileService()
        this.notificationService = new WebNotificationService()
    }
  }

  // 统一接口
  async saveFile(content: any, options: FileSaveOptions = {}): Promise<FileSaveResult> {
    return this.fileService.save(content, options)
  }

  async loadFile(filePath?: string): Promise<FileLoadResult> {
    return this.fileService.load(filePath)
  }

  async showNotification(title: string, body: string): Promise<void> {
    return this.notificationService.show(title, body)
  }

  isDesktop(): boolean {
    return this.platform === 'tauri' || this.platform === 'electron'
  }

  isWeb(): boolean {
    return this.platform === 'web'
  }

  getPlatform(): Platform {
    return this.platform
  }
}

// 单例模式
let platformServiceInstance: PlatformService | null = null

export function getPlatformService(): PlatformService {
  if (!platformServiceInstance) {
    platformServiceInstance = new PlatformService()
  }
  return platformServiceInstance
}

export default PlatformService

